import * as React from 'react'

import { NotFound } from '@factorialco/deprecated-design-system'

import { observer } from 'mobx-react'

import Loading from 'modules/core/components/Loading'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import i18n from 'modules/core/lib/i18n'

import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import { useShiftManagementModuleAvailability } from 'modules/shift_management/hooks/useShiftManagementModuleAvailability'
import templates from 'modules/shift_management/store/api/collections/shiftTemplates'
import templateWeeks from 'modules/shift_management/store/api/collections/templateWeeks'
import type TemplateWeek from 'modules/shift_management/store/api/models/TemplateWeek'

import WeekSettings from './WeekSettings'

interface ContentProps {
  templateWeek: TemplateWeek | null | undefined
  shiftTemplateId: string
}

const Content = ({ templateWeek, shiftTemplateId }: ContentProps) => {
  if (!templateWeek) return null

  return <WeekSettings templateWeek={templateWeek} shiftTemplateId={shiftTemplateId} />
}

interface UseFetchDataType {
  loading: boolean
  isNotFound: boolean
  templateWeek: TemplateWeek | null | undefined
}

function useFetchData(weekId: string): UseFetchDataType {
  const { isNotFound, isModuleLoading } = useShiftManagementModuleAvailability()

  const attr = {
    id: weekId,
  }

  const { loading } = useAsyncData(async () => {
    await templateWeeks.fetch()

    const templateWeek = templateWeeks.find(attr)
    const templateId = templateWeek?.attributes.template_id

    if (templateId) {
      await templates.fetch({
        data: {
          ids: [templateId],
        },
      })
    }
  }, [])

  const templateWeek = !loading ? templateWeeks.find(attr) : null

  return {
    loading: loading || isModuleLoading,
    templateWeek,
    isNotFound,
  }
}

interface Props {
  shiftTemplateId: string
  weekId: string
}

function EditTemplateWeek({ shiftTemplateId, weekId }: Props) {
  const { loading, isNotFound, templateWeek } = useFetchData(weekId)

  if (loading) {
    return <Loading />
  }

  if (isNotFound) {
    return (
      <NotFound
        title={i18n.t('errors.page_not_found.title')}
        description={i18n.t('errors.page_not_found.description')}
      />
    )
  }

  return (
    <ResponsivePage.Body showHeader={false}>
      <Content templateWeek={templateWeek} shiftTemplateId={shiftTemplateId} />
    </ResponsivePage.Body>
  )
}

export default observer(EditTemplateWeek)
